import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, FolderIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { FolderTreeNode } from '../../types/documentStorage';
import { Button } from '../common/Button';

interface MoveFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMove: (newParentId: string | null) => void;
  currentFolder: FolderTreeNode | null;
  allFolders: FolderTreeNode[];
}

const MoveFolderModal: React.FC<MoveFolderModalProps> = ({
  isOpen,
  onClose,
  onMove,
  currentFolder,
  allFolders
}) => {
  const { t } = useLanguage();
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen && currentFolder) {
      setSelectedParentId(currentFolder.parentId || null);
    }
  }, [isOpen, currentFolder]);

  // Filter out the current folder and its descendants to prevent circular references
  const getAvailableParents = (folders: FolderTreeNode[], excludeFolderId: string): FolderTreeNode[] => {
    const isDescendant = (folder: FolderTreeNode, ancestorId: string): boolean => {
      if (folder.id === ancestorId) return true;
      if (folder.parentId && folders.find(f => f.id === folder.parentId)) {
        const parent = folders.find(f => f.id === folder.parentId);
        return parent ? isDescendant(parent, ancestorId) : false;
      }
      return false;
    };

    return folders.filter(folder => 
      folder.id !== excludeFolderId && 
      !isDescendant(folder, excludeFolderId)
    );
  };

  const availableParents = currentFolder 
    ? getAvailableParents(allFolders, currentFolder.id)
    : allFolders;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentFolder) return;

    setLoading(true);
    try {
      await onMove(selectedParentId);
      onClose();
    } catch (error) {
      console.error('Error moving folder:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderFolderOption = (folder: FolderTreeNode, level: number = 0) => {
    const Icon = FolderIcon;
    return (
      <div key={folder.id}>
        <label
          className={`
            flex items-center px-3 py-2 cursor-pointer rounded-md transition-colors
            hover:bg-gray-50 dark:hover:bg-gray-700
            ${selectedParentId === folder.id ? 'bg-blue-50 dark:bg-blue-900/30' : ''}
          `}
          style={{ paddingLeft: `${12 + level * 16}px` }}
        >
          <input
            type="radio"
            name="parentFolder"
            value={folder.id}
            checked={selectedParentId === folder.id}
            onChange={() => setSelectedParentId(folder.id)}
            className="mr-3 text-blue-600 focus:ring-blue-500"
          />
          
          <div 
            className="flex items-center justify-center w-6 h-6 rounded mr-2"
            style={{ backgroundColor: folder.color + '20' }}
          >
            <Icon 
              className="h-4 w-4"
              style={{ color: folder.color }}
            />
          </div>
          
          <div className="flex-1">
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {folder.name}
            </div>
            {folder.description && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {folder.description}
              </div>
            )}
          </div>
        </label>
        
        {/* Render children */}
        {folder.children && folder.children.map(child => 
          renderFolderOption(child, level + 1)
        )}
      </div>
    );
  };

  if (!currentFolder) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100"
                  >
                    Mappa áthelyezése
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg mb-4">
                      <div 
                        className="flex items-center justify-center w-8 h-8 rounded mr-3"
                        style={{ backgroundColor: currentFolder.color + '20' }}
                      >
                        <FolderIcon 
                          className="h-5 w-5"
                          style={{ color: currentFolder.color }}
                        />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {currentFolder.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Áthelyezendő mappa
                        </div>
                      </div>
                    </div>

                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Új szülő mappa kiválasztása:
                    </label>

                    <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-2 max-h-64 overflow-y-auto">
                      {/* Root level option */}
                      <label className="flex items-center px-3 py-2 cursor-pointer rounded-md transition-colors hover:bg-gray-50 dark:hover:bg-gray-700">
                        <input
                          type="radio"
                          name="parentFolder"
                          value=""
                          checked={selectedParentId === null}
                          onChange={() => setSelectedParentId(null)}
                          className="mr-3 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          📁 Gyökér mappa (nincs szülő)
                        </div>
                      </label>

                      {/* Available parent folders */}
                      {availableParents.map(folder => renderFolderOption(folder))}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={loading}
                    >
                      Mégse
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading}
                    >
                      {loading ? 'Áthelyezés...' : 'Áthelyezés'}
                    </Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MoveFolderModal;
